import { useState, useEffect } from 'react'

export type Theme = 'dark' | 'light' | 'system'

export function useTheme(
  reverse = false,
  defaultTheme: Theme = 'system',
  storageKey = 'vite-ui-theme',
): 'light' | 'dark' {
  // Initialize the theme from localStorage or fallback to default.
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey) as Theme | null
    return stored ?? defaultTheme
  })

  // Refresh the theme based on media query changes (only if theme is "system").
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      // Only update if no explicit theme was set.
      const stored = localStorage.getItem(storageKey) as Theme | null
      if (stored === null || stored === 'system') {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [storageKey])

  // Compute the final theme, considering system mode and reverse flag.
  let resultingTheme: Theme

  if (reverse) {
    if (theme === 'system') {
      // Get system theme and reverse it.
      resultingTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'light'
        : 'dark'
    } else {
      resultingTheme = theme === 'dark' ? 'light' : 'dark'
    }
  } else if (theme === 'system') {
    resultingTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  } else {
    resultingTheme = theme
  }

  return resultingTheme
}
